import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, TextField, Button, Typography, 
  Container, InputAdornment, IconButton, Alert 
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { authService } from '../services/authService';
import { getFirstAccessiblePage } from '../constants/pages';
import { ROLES } from '../constants/roles';

const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });

  // Show why a forced logout happened (e.g. account updated, session expired).
  useEffect(() => {
    try {
      const reason = sessionStorage.getItem('logout_reason');
      if (reason) {
        setNotice(reason);
        sessionStorage.removeItem('logout_reason');
      }
    } catch {
      // Ignore storage failures.
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // 1. Send request to your backend
      const response = await api.post('/api/auth/login', credentials);
      
      // 2. Extract token and user data
      // Note: The mock returns access_token, not token
      const { access_token, user } = response.data;

      // 3. Save user and token using authService
      authService.saveUser(user, access_token);

      // 4. Fetch full user profile with permissions
      await authService.fetchUserProfile();

      // 5. Navigate to first accessible page based on permissions (admin goes to dashboard)
      let defaultRoute = '/dashboard';
      if (user.role !== ROLES.ADMIN) {
        const permissions = authService.getPermissions();
        defaultRoute = getFirstAccessiblePage(permissions);
      }
      navigate(defaultRoute);
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      height: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      bgcolor: '#f5f5f5' 
    }}>
      <Container maxWidth="xs">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              WMS
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Inventory Management System
            </Typography>
          </Box>

          {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              variant="outlined"
              margin="normal"
              required
              value={credentials.email}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              margin="normal"
              required
              value={credentials.password}
              onChange={handleChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock color="action" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ mt: 3, mb: 2, py: 1.5, fontWeight: 'bold' }}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;